import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { ThemeModeContext, ThemeMode, ResolvedTheme } from '../../../contexts/ThemeModeContext/ThemeModeContext'
import { STORAGE_KEY, THEME_MODE_VALUES } from './constants'

const getStoredThemeMode = (): ThemeMode => {
  const stored = localStorage.getItem(STORAGE_KEY)
  if (stored && THEME_MODE_VALUES.includes(stored)) {
    return stored as ThemeMode
  }
  return ThemeMode.Light
}

const getSystemPreference = (): ResolvedTheme => {
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return ResolvedTheme.Dark
  }
  return ResolvedTheme.Light
}

type Props = {
  children: React.ReactNode
}

export const ThemeModeProvider: FC<Props> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(getStoredThemeMode)
  const [systemPreference, setSystemPreference] = useState<ResolvedTheme>(getSystemPreference)

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent): void => {
      setSystemPreference(e.matches ? ResolvedTheme.Dark : ResolvedTheme.Light)
    }
    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [])

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode)
    localStorage.setItem(STORAGE_KEY, mode)
  }, [])

  const resolvedTheme = themeMode === ThemeMode.Auto ? systemPreference : (themeMode as unknown as ResolvedTheme)

  const value = useMemo(() => ({ themeMode, resolvedTheme, setThemeMode }), [themeMode, resolvedTheme, setThemeMode])

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>
}
